/**
 * Brand marks v3 — "The Wolf" (June 2026 identity).
 *
 * The studio mascot — a blue-grey wolf pup with a navy outline whose fur
 * palette sits natively on the site's --color-blue / --color-navy tokens —
 * replaces the geometric "A & B, Locked" monogram. Two-tier system:
 *
 *  - WolfMark  — the raster mark. Chrome scale (nav 28px, footer 40px),
 *    moments (error / contact card), and the "hero" 3D render reserved
 *    for the 404 feature. The lockup PNG (A + wolf + B in public/brand/)
 *    is reserved for OG cards and collateral — never in-site chrome.
 *  - WolfGlyph — one-color head silhouette traced from the mark's alpha
 *    channel. For microcaps-scale chrome: pinned tabs, sign-off rows.
 */

import Image from "next/image";

type WolfMarkProps = {
  className?: string;
  /** Accessible name; omit (default) to render decorative + aria-hidden. */
  label?: string;
  /** "lg" (418×512 2D mark) above ~64px; "hero" = the full 3D render for
   *  featured moments (404). */
  variant?: "sm" | "lg" | "hero";
  /** Set for above-the-fold placements (nav) to skip lazy loading. */
  priority?: boolean;
};

const SOURCES = {
  sm: { src: "/brand/amalbryx-ab-wolf-square.png", width: 144, height: 144 },
  lg: { src: "/brand/amalbryx-ab-wolf-square.png", width: 473, height: 473 },
  hero: { src: "/brand/amalbryx-ab-wolf-square.png", width: 473, height: 473 },
} as const;

export function WolfMark({
  className,
  label,
  variant = "sm",
  priority = false,
}: WolfMarkProps) {
  const { src, width, height } = SOURCES[variant];
  return (
    <Image
      src={src}
      width={width}
      height={height}
      alt={label ?? ""}
      aria-hidden={label ? undefined : true}
      priority={priority}
      className={className}
    />
  );
}

type WolfGlyphProps = React.SVGProps<SVGSVGElement> & {
  label?: string;
};

/**
 * Monoline head silhouette in currentColor. Same path ships as
 * public/safari-pinned-tab.svg for the Safari mask-icon.
 */
export function WolfGlyph({ className, label, ...rest }: WolfGlyphProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      width="64"
      height="64"
      xmlns="http://www.w3.org/2000/svg"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={className}
      {...rest}
    >
      <path
        d="M12 6 L24 19 L32 15 L40 19 L52 6 L53 24 L46 33 L52 38 L44 44 L38 52 L32 57 L26 52 L20 44 L12 38 L18 33 L11 24 Z"
        fill="currentColor"
      />
    </svg>
  );
}
